<script>
	import { scale } from 'svelte/transition';
	import { buyOrdaliePro } from '$lib/account.svelte.js';
	import { closeModal, openModal } from '$lib/modal-store';
	import { onMount } from 'svelte';
	import { userSession } from '$lib/stores.svelte.js';
	import Login from '$lib/components/login.svelte';
	import { t } from '$lib/i18n.svelte.js';
	import { getPromoCode, promoConfigs, standardPricing, calculateTTC } from '$lib/data/promoConfig';

	/** @type {'monthly' | 'yearly'} */
	let selectedPricePlan = $state('monthly');

	const buyPro = async () => {
		buyOrdaliePro(selectedPricePlan)
			.then((portal) => {
				window.location.href = portal.url;
			})
			.catch((error) => {
				console.error('Error buying Ordalie Pro: ', error);
			});
	};

	onMount(() => {
		if ($userSession == null) {
			openModal(Login);
		}
	});

	let activePromo = $derived(promoConfigs.find(p => p.id === getPromoCode()?.id));

	/** @param {'monthly' | 'yearly'} plan */
	const getPrice = (plan) => {
		const pricing = activePromo?.pricing || standardPricing;
		return plan === 'yearly' ? pricing.yearlyPrice : pricing.monthlyPrice;
	};

	/** @param {'monthly' | 'yearly'} plan */
	const getPriceTTC = (plan) => {
		return calculateTTC(getPrice(plan));
	};

	/** @param {'monthly' | 'yearly'} plan */
	const getOriginalPrice = (plan) => {
		if (!activePromo) return getPrice(plan);
		return plan === 'yearly' ? standardPricing.yearlyPrice : standardPricing.monthlyPrice;
	};

	/** @param {'monthly' | 'yearly'} plan */
	const getOriginalPriceTTC = (plan) => {
		return calculateTTC(getOriginalPrice(plan));
	};

	/** @param {'monthly' | 'yearly'} plan */
	const showDiscount = (plan) => {
		const pricing = activePromo?.pricing || standardPricing;
		return plan === 'yearly' && pricing.hasYearlyDiscount;
	};
</script>

<div
	role="dialog"
	class="modal z-40"
	in:scale|global={{ start: 0.95, duration: 500 }}
	out:scale|global={{ start: 0.95, duration: 300 }}
>
	<div
		class="bg-white dark:bg-ord-cardcolor-dark rounded-2xl z-50 mx-4 w-full max-w-(--breakpoint-md) shadow-xl transition-all relative dark:border dark:border-ord-border-dark pointer-events-auto"
	>
		<div class="max-h-[90vh] overflow-y-auto">
			<!-- Hero Section -->
			<section class="relative">
				<div class="relative pt-6 pb-8 px-6">
					<div class="flex flex-col items-center gap-6">
						{#if activePromo}
							<div class="flex items-center gap-6">
								<img src={activePromo.logo} alt="Partner logo" class="h-8 w-auto" />
								<div class="h-7 w-[1px] bg-gray-200 dark:bg-ord-border-dark"></div>
								<div class="bg-green-50 border border-green-100 rounded-full px-3 py-1 inline-flex items-center gap-2">
									<span class="text-green-700 font-medium text-base">-{activePromo.discount}%</span>
									<span class="h-3 w-[1px] bg-green-200"></span>
									<span class="text-green-600 text-sm font-medium">{activePromo.discountLabel}</span>
								</div>
							</div>
						{/if}

						<div class="text-center">
							<h3 class="text-2xl md:text-4xl font-medium text-black dark:text-white mb-4 leading-tight">
								{t('status.becomePro')}
							</h3>

							<div class="inline-flex bg-gray-100 dark:bg-ord-darkgrey p-1 rounded-full">
								<button
									onclick={() => (selectedPricePlan = 'monthly')}
									class="px-8 py-2 rounded-full text-[15px] font-medium leading-none transition-all pointer-events-auto {selectedPricePlan === 'monthly'
										? 'bg-white dark:bg-ord-cardcolor-dark shadow-xs'
										: 'text-gray-500 hover:text-gray-700'}"
								>
									{t('status.monthly')}
								</button>
								<button
									onclick={() => (selectedPricePlan = 'yearly')}
									class="px-8 py-2 rounded-full text-[15px] font-medium leading-none transition-all {selectedPricePlan === 'yearly'
										? 'bg-white dark:bg-ord-cardcolor-dark shadow-xs'
										: 'text-gray-500 hover:text-gray-700'}"
								>
									{t('status.yearly')}
									{#if showDiscount('yearly')}
										<span class="ml-1 text-xs bg-royal-blue text-white px-2 py-0.5 rounded-full">-20%</span>
									{/if}
								</button>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- Pricing Cards -->
			<div class="px-4 grid md:grid-cols-12 gap-6 mb-8">
				<!-- Free Plan -->
				<div class="col-span-5 hidden md:block">
					<div class="bg-gray-50 dark:bg-ord-darkgrey rounded-xl px-8 py-8 relative overflow-hidden border border-gray-200 dark:border-ord-border-dark">
						<div class="text-sm uppercase tracking-wider text-gray-500 mb-1.5 font-medium leading-none">{t('status.free')}</div>
						<h3 class="text-2xl font-medium mb-1.5 text-black dark:text-white leading-tight">{t('offer.freeForever1')}</h3>
						<div class="text-base text-gray-500 dark:text-gray-400 font-medium leading-snug">{t('offer.freeForever2')}</div>
						
						<div class="my-6">
							<div class="flex items-baseline gap-1">
								<span class="text-4xl font-medium tracking-tight text-black dark:text-white leading-none">0€</span>
								<span class="text-gray-500 text-base font-medium leading-none">{t('offer.freeIntro')}</span>
							</div>
						</div>

						<button class="w-full bg-gray-200 text-gray-700 rounded-lg py-2.5 mb-6 text-[15px] font-medium hover:bg-gray-300 transition-colors">
							{t('offer.actualOffer')}
						</button>

						<ul class="space-y-2.5 ml-0">
							{#each ['freeAIModel', 'freeQueries', 'freeHistoricalRecords', 'freeSearchEngine', 'freeCreateNotes'] as feature}
								<li class="flex items-start">
									<img src="/icons/v1/tick.svg" alt="" class="w-4 h-4 mr-2 mt-0.5 opacity-70 flex-none dark:invert" />
									<span class="text-base font-medium text-black dark:text-white leading-tight">{t(`offer.${feature}`)}</span>
								</li>
							{/each}
						</ul>
					</div>
				</div>

				<!-- Pro Plan -->
				<div class="col-span-7">
					<div class="bg-royal-blue text-white rounded-xl px-8 py-8 relative overflow-hidden border border-royal-blue">
						<div class="text-sm uppercase tracking-wider text-blue-200 mb-1.5 font-medium leading-none">{t('status.becomePro')}</div>
						<h3 class="text-2xl font-medium mb-1.5 leading-tight">{t('offer.proTitle')}</h3>
						<div class="text-base text-blue-200 font-medium leading-snug">{t('offer.proSubtitle')}</div>
						
						<div class="my-6">
							<div class="flex items-baseline gap-1 flex-wrap">
								{#if activePromo}
									<div class="flex items-baseline gap-1 min-w-[120px]">
										<span class="text-4xl font-medium tracking-tight leading-none">{getPriceTTC(selectedPricePlan)}€</span>
										<span class="text-xl line-through opacity-50 leading-none">{getOriginalPriceTTC(selectedPricePlan)}€</span>
									</div>
								{:else}
									<span class="text-4xl font-medium tracking-tight leading-none">{getPriceTTC(selectedPricePlan)}€</span>
								{/if}
								<span class="opacity-75 text-base whitespace-nowrap font-medium leading-none">{t('offer.proPriceCondition')}</span>
							</div>
						</div>

						<button
							onclick={buyPro}
							class="w-full bg-white text-royal-blue rounded-lg py-2.5 mb-6 text-[15px] font-medium group flex items-center justify-center hover:bg-gray-100 transition-colors"
						>
							{t('offer.proSouscriptionButton')}
							<img
								src="/icons/v2/arrow-left-02-stroke-rounded.svg"
								alt=""
								class="h-4 ml-2 rotate-180 group-hover:rotate-[135deg] transition"
							/>
						</button>

						<ul class="space-y-2.5 ml-0">
							{#each ['proAIModel', 'proQueries', 'proHistoricalRecords', 'proSearchEngine', 'proCreateNotes', 'proImportDocuments', 'proGenerateContracts', 'proAnalyseDocuments'] as feature}
								<li class="flex items-start">
									<img src="/icons/v1/tick.svg" alt="" class="w-4 h-4 mr-2 mt-0.5 invert opacity-70 flex-none" />
									<span class="text-base font-medium leading-tight">{t(`offer.${feature}`)}</span>
								</li>
							{/each}
						</ul>
					</div>
				</div>
			</div>

			<div class="text-center text-sm text-gray-500 pb-3">
				<p>{t('status.vatExcluded')}</p>
			</div>
		</div>

		<!-- Footer -->
		<div class="w-full bg-ord-grey dark:bg-ord-cardcolor-dark p-2 rounded-b-2xl border-t border-grey-stroke-signature dark:border-ord-border-dark">
			<p class="w-full text-center text-xs md:text-sm opacity-75 text-black dark:text-white">
				{t('offer.customizedNeed1')} <br class="md:hidden" />
				<a class="underline" href="mailto:support@ordalie.ai"> {t('offer.customizedNeed2')}</a>
			</p>
		</div>
	</div>
</div>

<style>
	.modal {
		position: fixed;
		top: 0;
		bottom: 0;
		right: 0;
		left: 0;
		display: flex;
		justify-content: center;
		align-items: center;
	}
</style>
